// Explicit .js extension: `api/quest-art.ts` reaches this module, and the
// deployed serverless bundle is ESM (root package.json is `"type": "module"`),
// where Node does no extension guessing. Extensionless here crashed
// /api/quest-art at cold boot. Vite and vitest resolve this form fine.
import { SKILL_TAXONOMY } from "../skills.js";

/**
 * The eight categories the filter UI offers. They live HERE, in the module
 * that owns the derivation, rather than in `questCategory.ts`: that module
 * needs `deriveQuestScene` to narrow a scene down to a category, and if this
 * module imported the keys back from it, the cycle would reach these very
 * declarations inside their temporal dead zone — a ReferenceError at import
 * time rather than a warning. `questCategory.ts` re-exports them, so every
 * existing import keeps working.
 */
export const QUEST_CATEGORY_KEYS = [
  "development",
  "design",
  "writing",
  "research",
  "learning",
  "marketing",
  "data",
  "other",
] as const;

export type QuestCategory = (typeof QUEST_CATEGORY_KEYS)[number];

const KNOWN: ReadonlySet<string> = new Set<string>(QUEST_CATEGORY_KEYS);

/**
 * Whether the stored category is one we can actually key art off, as opposed
 * to the DEFAULT '' or the agent's "general". Distinguishing this from
 * `categoryKey` matters: `categoryKey` cannot tell "the quest really is Other"
 * from "we have no idea", and only the second case should consult the skills.
 */
export function isKnownCategory(raw: string | null | undefined): boolean {
  return KNOWN.has((raw ?? "").trim().toLowerCase());
}

/** Normalise a stored category to a known key, falling back to `other`. */
export function categoryKey(raw: string | null | undefined): QuestCategory {
  const k = (raw ?? "").trim().toLowerCase();
  return (KNOWN.has(k) ? k : "other") as QuestCategory;
}

/**
 * Which picture a quest card draws.
 *
 * A SCENE is what gets drawn; a CATEGORY is what the filter UI offers. Scenes
 * are a superset of the eight categories, so the art can gain a subject
 * without growing the user-facing taxonomy — `QUEST_CATEGORIES`,
 * `QuestEditModal` and the filter chips all stay at eight.
 *
 * Two scenes exist beyond the categories:
 *
 *  - `photography`, because photo work is common on this marketplace — three
 *    of the twelve quests in the reported /discover feed — and every one of
 *    them rendered the generic toolbox, since `SKILL_TAXONOMY` files
 *    Photography under its coarse `Other` bucket.
 *  - `errands`, for on-the-ground physical tasks, which had the same problem.
 *
 * This module owns the quest-art skill-matching table. The profile radar has
 * its own, broader one in `skillDomains.ts` (work domains, not card art); a
 * stem added to one does not reach the other.
 * `deriveQuestCategory` narrows a scene through `SCENE_TO_CATEGORY`, so the
 * art and the filter can never disagree about what a quest is about.
 */
export const QUEST_SCENE_KEYS = [
  ...QUEST_CATEGORY_KEYS,
  "photography",
  "errands",
] as const;

export type QuestScene = (typeof QUEST_SCENE_KEYS)[number];

/**
 * Every scene's home category, for the filter. The eight category scenes map
 * to themselves; the two extra scenes belong to `other`, which is exactly what
 * their skills used to resolve to before they had pictures of their own.
 */
export const SCENE_TO_CATEGORY: Record<QuestScene, QuestCategory> = {
  development: "development",
  design: "design",
  writing: "writing",
  research: "research",
  learning: "learning",
  marketing: "marketing",
  data: "data",
  other: "other",
  photography: "other",
  errands: "other",
};

/** How many variants each scene has art for. Guarded against drift by artRegistry.test.ts. */
export const SCENE_VARIANT_COUNT: Record<QuestScene, number> = {
  development: 3,
  design: 3,
  writing: 1,
  research: 1,
  learning: 1,
  marketing: 3,
  data: 1,
  other: 1,
  photography: 1,
  errands: 1,
};

/**
 * Lowercase, FOLD accents, strip separators: "Lead Generation" /
 * "lead-generation" -> "leadgeneration", "plomería" -> "plomeria".
 *
 * DEV-855 — the folding step is new, and it is the difference between the
 * Spanish rules below working and being dead code. Stripping accents as
 * "characters outside a-z" turned "plomería" into "plomera" and "cañerías"
 * into "caeras": no natural pattern matches those, so every rule written in
 * Spanish would have to be misspelled to fire. NFD decomposes the accent into
 * a combining mark, which the strip then removes from the base letter.
 */
function normalize(raw: string): string {
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Scene-only rules, checked BEFORE the taxonomy index — and that order is the
 * whole point. `normalize("Photography")` equals `normalize("photography")`,
 * so an exact taxonomy hit would resolve photo work to its `Other` bucket and
 * the dedicated scene would be unreachable. These patterns are deliberately
 * more specific than the taxonomy's coarse buckets, so they win.
 *
 * Checked against every skill in `ALL_SKILLS` for accidental capture: none of
 * these substrings appears in any other taxonomy skill.
 */
const SCENE_RULES: ReadonlyArray<readonly [string, QuestScene]> = [
  ["photo", "photography"], // Photography, photo-editing, photoshoot
  ["foto", "photography"], // fotografía, fotografia, fotógrafo
  ["camera", "photography"],
  ["camara", "photography"],
  ["selfie", "photography"],
  ["errand", "errands"],
  ["delivery", "errands"],
  ["courier", "errands"],
  ["plumb", "errands"],
  ["handyman", "errands"],
  ["onsite", "errands"],
  // DEV-855 — the Spanish and Portuguese half. Every one of these is a job
  // this marketplace has actually posted or plainly will; the plumbing quest
  // that exposed the gap tagged itself "plomería, fontanería, reparación de
  // cañerías" and drew the generic fallback.
  ["plomer", "errands"], // plomería, plomero
  ["fontaner", "errands"], // fontanería (Spain)
  ["encanad", "errands"], // encanador (pt)
  ["caneria", "errands"], // cañería
  ["tuberia", "errands"], // tubería
  ["gasfiter", "errands"],
  ["electricista", "errands"],
  ["cerrajer", "errands"],
  ["albanil", "errands"], // albañil
  ["mudanza", "errands"],
  ["domicilio", "errands"], // entrega a domicilio
  ["mensajer", "errands"], // mensajería
  ["presencial", "errands"],
  ["entrega", "errands"],
  ["limpieza", "errands"], // limpieza, limpeza
  ["limpeza", "errands"],
  ["lavado", "errands"], // lavado de autos
  ["physicallabor", "errands"],
];

/**
 * The scene each SKILL_TAXONOMY category draws. Most categories ARE a scene;
 * the rest borrow the closest one. Without this, a taxonomy category that is
 * not a scene name ("Languages") resolved to a scene that does not exist and
 * `deriveQuestCategory` returned undefined.
 */
export const TAXONOMY_CATEGORY_SCENE: Readonly<Record<string, QuestScene>> = {
  Development: "development",
  Design: "design",
  Writing: "writing",
  Research: "research",
  Marketing: "marketing",
  Data: "data",
  Learning: "learning",
  Languages: "learning",
  Home: "errands",
  Care: "errands",
  Local: "errands",
  Music: "learning",
  Sports: "learning",
  Events: "errands",
  Other: "other",
};

/**
 * Exact skill -> category, straight from SKILL_TAXONOMY.
 *
 * The taxonomy already groups every first-party skill under the eight
 * categories, so it is the source of truth for them. Keys are normalised
 * because the agent writes kebab-case free text ("graphic-design") while the
 * taxonomy uses display casing ("Graphic Design").
 */
const TAXONOMY_INDEX: ReadonlyMap<string, QuestScene> = new Map(
  Object.entries(SKILL_TAXONOMY).flatMap(([category, skills]) =>
    skills.map(
      (skill) =>
        [normalize(skill), TAXONOMY_CATEGORY_SCENE[category] ?? "other"] as const,
    ),
  ),
);

/**
 * Substring rules for the free-form skills the agent invents outside the
 * taxonomy — "lead-generation", "product-hunt", "no-code", "html-css". Matched
 * against a normalised skill, FIRST match wins, so order only matters where
 * one skill contains two keywords; each such case is called out below.
 */
const KEYWORD_RULES: ReadonlyArray<readonly [string, QuestScene]> = [
  // Development first: "frontend-development" and "web-development" must not
  // be captured by the "design" rule further down.
  ["frontend", "development"],
  ["backend", "development"],
  ["fullstack", "development"],
  ["develop", "development"],
  ["desarroll", "development"], // desarrollo, desarrollador
  ["programac", "development"], // programación
  ["engineer", "development"],
  ["code", "development"], // no-code, low-code, coding
  ["programming", "development"],
  ["html", "development"],
  ["css", "development"],
  ["javascript", "development"],
  ["typescript", "development"],
  ["react", "development"],
  ["api", "development"],
  ["scraping", "development"],
  ["automation", "development"],

  ["design", "design"],
  ["diseno", "design"], // diseño
  ["logo", "design"],
  ["illustrat", "design"], // illustrator, illustration
  ["figma", "design"],
  ["brand", "design"],
  ["video", "design"], // the taxonomy files Video Editing under Design
  ["animation", "design"],
  ["motion", "design"],

  // Before the generic "content" writing rule: content STRATEGY is marketing.
  ["contentstrategy", "marketing"],
  ["copywrit", "writing"],
  ["writing", "writing"],
  ["redacc", "writing"], // redacción
  ["redator", "writing"], // pt
  ["escrib", "writing"], // escribir, escritura
  ["traduc", "writing"], // traducción, tradução
  ["conteni", "writing"], // contenido
  ["conteud", "writing"], // conteúdo
  ["blog", "writing"],
  ["seo", "writing"],
  ["script", "writing"],
  ["translat", "writing"],
  ["proofread", "writing"],
  ["content", "writing"],

  ["marketing", "marketing"],
  ["mercadeo", "marketing"],
  ["publicid", "marketing"], // publicidad, publicidade
  ["anuncio", "marketing"],
  ["outreach", "marketing"],
  ["lead", "marketing"],
  ["growth", "marketing"],
  ["community", "marketing"],
  ["social", "marketing"],
  ["advertis", "marketing"],
  ["ads", "marketing"],
  ["campaign", "marketing"],
  ["influencer", "marketing"],
  ["producthunt", "marketing"],
  ["launch", "marketing"],

  ["research", "research"],
  // DEV-855 — user testing is research, and it is the single most common
  // skill in the live corpus (8 of 32 quests) with no rule to place it.
  ["usertest", "research"],
  ["pruebadeusuario", "research"],
  ["pruebasdeusuario", "research"],
  ["analysis", "research"],
  ["analisis", "research"], // análisis
  ["investigac", "research"], // investigación
  ["competitive", "research"],
  ["survey", "research"],
  ["encuesta", "research"],

  ["data", "data"],
  ["sql", "data"],
  ["spreadsheet", "data"],
  ["etl", "data"],
  ["machinelearning", "data"],
  ["visualization", "data"],
  ["dashboard", "data"],

  ["tutor", "learning"],
  ["ensen", "learning"], // enseñanza, enseñar
  ["capacitac", "learning"], // capacitación
  ["curso", "learning"],
  ["teach", "learning"],
  ["mentor", "learning"],
  ["course", "learning"],
  ["training", "learning"],
  ["language", "learning"],
  ["chinese", "learning"],
  ["spanish", "learning"],
  ["english", "learning"],
  ["music", "learning"],

  ["audio", "other"],
  ["transcription", "other"],
  ["support", "other"],
];

/** One skill's scene, or null when we genuinely cannot place it. */
function skillScene(skill: string): QuestScene | null {
  const key = normalize(skill);
  if (!key) return null;

  for (const [pattern, scene] of SCENE_RULES) {
    if (key.includes(pattern)) return scene;
  }

  const exact = TAXONOMY_INDEX.get(key);
  if (exact) return exact;

  for (const [pattern, scene] of KEYWORD_RULES) {
    if (key.includes(pattern)) return scene;
  }
  return null;
}

/**
 * The scene to draw for a quest.
 *
 * A category that really is in the closed set — a quest edited through
 * QuestEditModal, or a mock fixture — is trusted as-is. Otherwise the quest's
 * skills vote and the majority wins; ties go to the earliest-listed skill,
 * since the agent emits skills roughly in order of relevance.
 *
 * Unplaceable skills ABSTAIN rather than voting `other`, so one stray tag
 * cannot outvote two meaningful ones. `other` is returned only when nothing
 * places at all — it is a safety net, and the moment it becomes a default the
 * whole feed renders one picture, which is the bug this design exists to fix.
 */
export function deriveQuestScene(
  storedCategory: string | null | undefined,
  skills: readonly string[] | null | undefined,
): QuestScene {
  if (isKnownCategory(storedCategory)) return categoryKey(storedCategory);

  const votes = new Map<QuestScene, { count: number; first: number }>();

  (skills ?? []).forEach((skill, index) => {
    const scene = skillScene(skill);
    if (!scene) return;
    const tally = votes.get(scene);
    if (tally) tally.count += 1;
    else votes.set(scene, { count: 1, first: index });
  });

  let winner: QuestScene = "other";
  let best = { count: 0, first: Number.POSITIVE_INFINITY };
  for (const [scene, tally] of votes) {
    if (
      tally.count > best.count ||
      (tally.count === best.count && tally.first < best.first)
    ) {
      winner = scene;
      best = tally;
    }
  }
  return winner;
}

/**
 * Stable variant index for a quest.
 *
 * FNV-1a over the id, so a quest's art is its own — the same in the swipe
 * card, the share card and the detail page. The feed layer may override it via
 * `resolveVariants` so two identical scenes never sit side by side.
 */
export function variantFor(questId: string, count: number): number {
  if (count <= 1) return 0;
  let hash = 0x811c9dc5;
  for (let i = 0; i < questId.length; i += 1) {
    hash ^= questId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % count;
}

/**
 * Picks each card's variant for a rendered FEED, so no two neighbours draw the
 * identical picture.
 *
 * `variantFor` alone cannot do this, and no amount of extra art fixes it: with
 * three variants, three same-scene quests in a row still collide about 78% of
 * the time (four variants is still ~62%), because a hash of an id cannot see
 * what is beside it. Per-quest stability and never-repeating-adjacently are in
 * genuine conflict, so the hash provides the default and this pass resolves the
 * conflict at render order — nudging only the cards that would actually repeat.
 *
 * The accepted cost is that a card's variant can change when the feed is
 * re-sorted or filtered, and may differ from the same quest's detail page. The
 * art is `aria-hidden` decoration whose information is already carried by the
 * title, so this is a presentation detail rather than a fact shifting under the
 * reader.
 *
 * `counts` is injectable for tests; it defaults to the shipped registry. This
 * function stays pure and never imports the art, or the feed hook would pull
 * every SVG into the data layer.
 */
export function resolveVariants(
  cards: ReadonlyArray<{ id: string; artScene: QuestScene }>,
  counts: Partial<Record<QuestScene, number>> = SCENE_VARIANT_COUNT,
): number[] {
  const out: number[] = [];
  let prevScene: QuestScene | null = null;
  let prevVariant = -1;

  for (const c of cards) {
    const count = counts[c.artScene] ?? 1;
    let v = variantFor(c.id, count);
    // Only when the neighbour would render the identical picture. A shared
    // index across two DIFFERENT scenes is not a repeat.
    if (c.artScene === prevScene && v === prevVariant && count > 1) {
      v = (v + 1) % count;
    }
    out.push(v);
    prevScene = c.artScene;
    prevVariant = v;
  }
  return out;
}
